import React from "react";
import {
  Avatar,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { Clock, Search } from "lucide-react";

type SkillItem = {
  id: string;
  name: string;
  logo: string;
  /** 更新时间，ISO 或 YYYY-MM-DD，展示为 YYYY.MM.DD */
  updatedAt?: string;
  shortDescription: string;
  scenarioDescription: string;
  prompt: string;
  category: "增长获客" | "内容运营" | "客户服务" | "销售转化";
  // 接口字段可能为空，缺失时使用默认“开发者”信息兜底。
  developerName?: string;
  developerLogo?: string;
};

const DEFAULT_DEVELOPER_NAME = "3Chat.ai";
// public/ 下的静态资源在页面里通过 /images/... 访问
const DEFAULT_DEVELOPER_AVATAR_SRC = "/images/3Chat_circle_black.svg";
const SKILL_LOGO_BASE_PATH = "/images/skill_store";
/** 文件名含空格，用编码路径避免加载问题 */
const BANNER_AI_EMPLOYEES_SRC = `${SKILL_LOGO_BASE_PATH}/banner1-AI%20employees.png`;
const BANNER_7X24_SRC = `${SKILL_LOGO_BASE_PATH}/banner1-7x24.png`;
const BANNER2_LIVECHAT_SRC = `${SKILL_LOGO_BASE_PATH}/banner2-livechat.png`;
const BANNER2_CURSOR_SRC = `${SKILL_LOGO_BASE_PATH}/banner2-cursor.png`;
type BannerVisual = {
  id: string;
  src: string;
  alt: string;
  className: string;
  animated?: boolean;
  animationScale?: number;
};

type TopBannerItem = {
  id: string;
  title: string;
  description: string;
  backgroundClassName: string;
  visuals: BannerVisual[];
};

const TOP_BANNERS: TopBannerItem[] = [
  {
    id: "growth",
    title: "点一下\nAI 员工帮你赚钱",
    description: "安装一个技能，就像招一个 AI 员工，自动获客、互动并完成转化",
    backgroundClassName: "bg-gradient-to-br from-[#DEF6FD] to-[#ACD2F1]",
    visuals: [
      {
        id: "growth-7x24",
        src: BANNER_7X24_SRC,
        alt: "7×24",
        animated: true,
        animationScale: 1.8,
        className:
          "pointer-events-none absolute right-1 top-2 z-0 h-24 w-auto origin-top-right select-none object-contain sm:right-10 sm:top-3 sm:h-24 md:right-10 md:top-4 md:h-28",
      },
      {
        id: "growth-ai",
        src: BANNER_AI_EMPLOYEES_SRC,
        alt: "",
        className:
          "pointer-events-none absolute bottom-0 right-0 z-10 h-[200px] w-auto max-w-none translate-y-14 select-none object-contain object-bottom sm:h-[260px] sm:right-20 sm:translate-y-14 md:h-[260px] md:right-20 md:translate-y-14",
      },
    ],
  },
  {
    id: "install-skill",
    title: "一句话\n安装你的 AI 技能",
    description: "浏览技能 → 点击安装 → 通过对话自动启动 AI，无需任何配置",
    backgroundClassName: "bg-gradient-to-br from-[#F5FFE7] to-[#FCEBC1]",
    visuals: [
      {
        id: "install-livechat",
        src: BANNER2_LIVECHAT_SRC,
        alt: "live chat",
        className:
          "pointer-events-none absolute bottom-0 right-0 z-10 h-[200px] w-auto max-w-none translate-y-3 select-none object-contain object-bottom sm:h-[240px] sm:right-10 sm:translate-y-3 md:h-[240px] md:right-10 md:translate-y-3",
      },
      {
        id: "install-cursor",
        src: BANNER2_CURSOR_SRC,
        alt: "cursor",
        animated: true,
        animationScale: 1.8,
        className:
          "pointer-events-none absolute right-20 top-16 z-20 h-20 w-auto origin-top-right select-none object-contain sm:right-20 sm:top-16 sm:h-20 md:right-20 md:top-16 md:h-20",
      },
    ],
  },
];

function splitBannerTitle(title: string) {
  const [line1, ...rest] = title.split("\n");
  return {
    line1: line1?.trim() || "",
    line2: rest.join("\n").trim(),
  };
}

function resolveSkillLogoSrc(skill: SkillItem) {
  // 优先使用后端传来的文件名，例如 "xhs-growth-starter.svg" / "xhs-growth-starter.png"
  const trimmedLogo = skill.logo?.trim();
  if (trimmedLogo && trimmedLogo.includes(".")) {
    return `${SKILL_LOGO_BASE_PATH}/${trimmedLogo}`;
  }

  // 否则按约定使用 PNG：{id}.png
  return `${SKILL_LOGO_BASE_PATH}/${skill.id}.png`;
}

function looksLikeImageAssetOrUrl(value: string) {
  const v = value.trim();
  if (!v) return false;
  if (v.startsWith("http://") || v.startsWith("https://")) return true;
  if (v.startsWith("/") || v.startsWith("data:image/")) return true;
  // 兜底识别：例如 /images/xxx.png 或 xxx.svg
  return (
    v.includes(".svg") ||
    v.includes(".png") ||
    v.includes(".jpg") ||
    v.includes(".jpeg") ||
    v.includes(".webp") ||
    v.includes("images/")
  );
}

function resolveDeveloperAvatarSrc(developerLogo?: string) {
  // 缺失时使用默认头像
  if (!developerLogo || !developerLogo.trim()) return DEFAULT_DEVELOPER_AVATAR_SRC;
  const v = developerLogo.trim();
  // developerLogo 在 mock/接口里可能是“缩写字符串”（如 GL、SN），此时回退到默认头像
  return looksLikeImageAssetOrUrl(v) ? v : DEFAULT_DEVELOPER_AVATAR_SRC;
}

function resolveDeveloperName(developerName?: string) {
  return developerName?.trim() || DEFAULT_DEVELOPER_NAME;
}

function resolveDeveloperAvatarName(developerLogo?: string, developerName?: string) {
  const resolvedName = resolveDeveloperName(developerName);
  const avatarSrc = resolveDeveloperAvatarSrc(developerLogo);

  // 当 avatar 使用图片时，name 用于兜底/无障碍文本；
  // 当 avatar 使用“缩写字符串”时，name 显示缩写本身。
  return avatarSrc ? resolvedName : developerLogo?.trim() || resolvedName;
}

function formatSkillUpdatedAt(isoOrDate: string | undefined): string {
  if (!isoOrDate?.trim()) return "2026.04.01";
  const s = isoOrDate.trim();
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (ymd) return `${ymd[1]}.${ymd[2]}.${ymd[3]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "2026.04.01";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

const SKILLS: SkillItem[] = [
  {
    id: "xhs-growth-starter",
    name: "小红书流量截胡引擎 · 本地生活高客单版（超长标题展示示例）",
    logo: "📡",
    updatedAt: "2026-04-23",
    shortDescription:
      "自动捕捉竞品评论区高意向用户，建立稳定引流入口，并根据地域、客单价与行业词包动态调整话术节奏，持续优化私信转化路径（超长描述展示示例）。",
    scenarioDescription:
      "适合本地服务、电商品牌和咨询业务。每天自动巡检热门帖与竞品账号，识别高意向关键词并执行首轮互动，帮团队把人工巡帖成本降到最低。",
    prompt:
      "你是增长策略专家。请围绕目标行业、客单价和地域，设计一个 7 天的评论区截流策略，输出：关键词池、评论模板、私信跟进节点、风险规避清单。",
    category: "增长获客",
    developerName: "3Chat.ai",
    developerLogo: "",
  },
  {
    id: "douyin-content-pilot",
    name: "抖音内容领航员",
    logo: "🎬",
    updatedAt: "2026-04-18",
    shortDescription: "根据品类和受众，生成可执行的短视频选题与脚本矩阵。",
    scenarioDescription:
      "适合需要持续产出视频内容的品牌团队。输入业务关键词后，系统可自动给出爆款结构、镜头节奏、文案钩子与发布时段建议。",
    prompt:
      "你是一名资深短视频编导。基于给定品类与用户画像，生成 10 条选题，每条包含：开场钩子、15 秒脚本、镜头建议、发布标题和评论区互动话术。",
    category: "内容运营",
    developerName: "3Chat.ai",
    developerLogo: "",
  },
  {
    id: "wechat-service-copilot",
    name: "私域客服 Copilot",
    logo: "💬",
    updatedAt: "2026-03-30",
    shortDescription: "多轮对话自动分流客户问题，兼顾效率与服务体验。",
    scenarioDescription:
      "适合微信、企微等私域场景。可自动识别咨询意图、统一话术风格并触发不同 SOP，提升客服响应速度并降低漏单风险。",
    prompt:
      "你是一名高级客服主管。请根据用户咨询语句判断意图类型（价格、交付、售后、投诉等），并输出：标准回复、升级处理条件、下一步行动建议。",
    category: "客户服务",
    developerName: "3Chat.ai",
    developerLogo: "",
  },
  {
    id: "sales-close-master",
    name: "成交推进大师",
    logo: "🏆",
    updatedAt: "2026-04-12",
    shortDescription: "识别客户购买阶段，自动给出高转化推进策略。",
    scenarioDescription:
      "适合高客单价业务和顾问式销售。模型会基于对话上下文判断用户顾虑，并提供分阶段成交话术、异议处理建议和促单节奏。",
    prompt:
      "你是顶级销售教练。请分析客户当前所处阶段，并给出 3 条推进策略：价值强化、异议消除、行动召回。语气专业、克制、可执行。",
    category: "销售转化",
    developerName: "3Chat.ai",
    developerLogo: "",
  },
  {
    id: "weekly-growth-report",
    name: "增长周报自动化",
    logo: "📈",
    updatedAt: "2026-02-28",
    shortDescription: "自动汇总渠道效果，输出管理层可读的增长复盘。",
    scenarioDescription:
      "适合需要高频复盘的运营团队。系统会按渠道、动作、成本和线索质量进行归因分析，并提供下周优化建议。",
    prompt:
      "你是数据策略顾问。根据本周渠道数据输出复盘报告，包括：核心结论、异常指标、优化动作、下周实验计划。",
    category: "增长获客",
    developerName: "3Chat.ai",
    developerLogo: "",
  },
  {
    id: "brand-tone-keeper",
    name: "品牌语调守门员",
    logo: "🧭",
    updatedAt: "2026-04-05",
    shortDescription: "统一跨渠道文案语调，避免内容风格漂移。",
    scenarioDescription:
      "适合品牌矩阵账号与多人协同团队。输入品牌核心价值后，可自动校正文案一致性并标注不符合调性的内容片段。",
    prompt:
      "你是品牌编辑总监。请检查以下文案是否符合品牌语调，并输出：不一致点、优化建议、重写后的最终版本。",
    category: "内容运营",
    developerName: "3Chat.ai",
    developerLogo: "",
  },
];

const categories = ["全部", ...Array.from(new Set(SKILLS.map((item) => item.category)))] as const;

export default function SkillStorePage() {
  const [keyword, setKeyword] = React.useState("");
  const [selectedCategory, setSelectedCategory] = React.useState<(typeof categories)[number]>("全部");
  const [activeSkill, setActiveSkill] = React.useState<SkillItem | null>(null);
  const [activeBannerIndex, setActiveBannerIndex] = React.useState(0);

  /** 7x24 装饰图：每次挂载随机周期与相位，轻微循环浮动 */
  const banner724Float = React.useMemo(
    () => ({
      duration: 3.6 + Math.random() * 2.4,
      delay: Math.random() * 0.9,
      yAmp: 3 + Math.random() * 3,
      xAmp: 1 + Math.random() * 1.5,
    }),
    [],
  );

  React.useEffect(() => {
    if (typeof document === "undefined") return;

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    if (activeSkill) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalBodyOverflow || "";
      document.documentElement.style.overflow = originalHtmlOverflow || "";
    }

    return () => {
      document.body.style.overflow = originalBodyOverflow || "";
      document.documentElement.style.overflow = originalHtmlOverflow || "";
    };
  }, [activeSkill]);

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveBannerIndex((prev) => (prev + 1) % TOP_BANNERS.length);
    }, 4500);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const filteredSkills = React.useMemo(() => {
    return SKILLS.filter((item) => {
      const matchCategory = selectedCategory === "全部" || item.category === selectedCategory;
      const matchKeyword =
        !keyword.trim() ||
        item.name.toLowerCase().includes(keyword.toLowerCase()) ||
        item.shortDescription.toLowerCase().includes(keyword.toLowerCase()) ||
        (item.developerName ?? "").toLowerCase().includes(keyword.toLowerCase());
      return matchCategory && matchKeyword;
    });
  }, [keyword, selectedCategory]);

  return (
    <div className="bg-background min-h-screen min-h-dvh w-full min-w-0 overflow-x-hidden">
      {/* overflow-x-hidden：避免顶部绝对定位大宽度光斑（w-[760px]）把文档横向撑开，导致移动端右侧出现“空白条” */}
      <div className="relative mx-auto w-full min-w-0 max-w-7xl overflow-x-hidden px-4 pb-20 pt-4 md:px-10 md:pb-24 md:pt-8">
        <div className="from-primary/35 via-secondary/20 pointer-events-none absolute left-1/2 top-[-180px] h-[420px] w-[min(760px,200vw)] max-w-[100vw] -translate-x-1/2 rounded-full bg-gradient-to-b to-transparent blur-3xl" />

        <LazyMotion features={domAnimation}>
          <m.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <div className="relative flex flex-col gap-4">
              <div className="relative h-[180px] w-full overflow-hidden rounded-[12px]" role="region" aria-label="技能商店轮播">
                <div
                  className="flex h-full w-full transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${activeBannerIndex * 100}%)` }}
                >
                  {TOP_BANNERS.map((banner) => {
                    const { line1, line2 } = splitBannerTitle(banner.title);
                    return (
                      <div
                        key={banner.id}
                        className={`flex h-full min-w-full flex-row items-stretch ${banner.backgroundClassName}`}
                      >
                        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 py-3 pl-5 pr-2 md:pl-8">
                          <p className="text-foreground text-[24px] font-bold leading-tight">{line1}</p>
                          <h1 className="text-foreground line-clamp-2 text-[24px] font-bold tracking-tight">
                            {line2}
                          </h1>
                          <p className="text-default-500 line-clamp-2 pt-2 text-sm leading-snug md:text-sm">
                            {banner.description}
                          </p>
                        </div>

                        <div className="relative h-full w-[min(46%,200px)] shrink-0 overflow-hidden sm:w-[340px] md:w-[340px]">
                          {banner.visuals.map((visual) =>
                            visual.animated ? (
                              <m.img
                                key={visual.id}
                                src={visual.src}
                                alt={visual.alt}
                                className={visual.className}
                                decoding="async"
                                animate={{
                                  y: [
                                    0,
                                    -banner724Float.yAmp * (visual.animationScale ?? 1),
                                    banner724Float.xAmp * 0.5 * (visual.animationScale ?? 1),
                                    -banner724Float.yAmp * 0.6 * (visual.animationScale ?? 1),
                                    0,
                                  ],
                                  x: [
                                    0,
                                    banner724Float.xAmp * (visual.animationScale ?? 1),
                                    -banner724Float.xAmp * 0.4 * (visual.animationScale ?? 1),
                                    banner724Float.xAmp * 0.3 * (visual.animationScale ?? 1),
                                    0,
                                  ],
                                }}
                                transition={{
                                  duration: banner724Float.duration,
                                  delay: banner724Float.delay,
                                  repeat: Infinity,
                                  ease: "easeInOut",
                                }}
                              />
                            ) : (
                              <img
                                key={visual.id}
                                src={visual.src}
                                alt={visual.alt}
                                className={visual.className}
                                decoding="async"
                              />
                            ),
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="absolute bottom-3 left-5 z-20 flex items-center gap-2 md:left-8">
                  {TOP_BANNERS.map((banner, index) => (
                    <button
                      key={banner.id}
                      type="button"
                      aria-label={`切换到第 ${index + 1} 张 banner`}
                      aria-pressed={activeBannerIndex === index}
                      onClick={() => setActiveBannerIndex(index)}
                      className={`h-1.5 rounded-full transition-all ${
                        activeBannerIndex === index
                          ? "w-6 bg-foreground/80"
                          : "w-2 bg-foreground/30 hover:bg-foreground/50"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </m.section>

          <m.section
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="mt-6 space-y-6"
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <Input
                value={keyword}
                variant="flat"
                size="md"
                color="default"
                onValueChange={setKeyword}
                startContent={<Search className="h-4 w-4 text-default-500" />}
                placeholder="按技能名称或开发者搜索"
                className="w-full min-w-0 md:max-w-[380px]"
                classNames={{
                  // 鼠标点击时往往没有 :focus-visible，ring 仍可能通过其它层叠出现；同时去掉原生 input 的 outline
                  input:
                    "outline-none focus:outline-none focus-visible:outline-none [outline-style:none] [outline-width:0]",
                  inputWrapper:
                    "border-default-200 bg-default-100 shadow-none " +
                    // 去掉 HeroUI flat 的 ring + ring-offset（白缝来自 ring-offset-background）
                    "group-data-[focus-visible=true]:!ring-0 group-data-[focus-visible=true]:!ring-offset-0 " +
                    "group-data-[focus=true]:!ring-0 group-data-[focus=true]:!ring-offset-0 " +
                    "data-[focus-visible=true]:!ring-0 data-[focus-visible=true]:!ring-offset-0",
                }}
              />

            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredSkills.map((item, index) => (
                <m.div
                  key={item.id}
                  className="h-full"
                  initial={{ opacity: 0, y: 22 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -4, transition: { duration: 0.25, ease: "easeOut" } }}
                  transition={{ duration: 0.35, delay: 0.03 * index }}
                >
                  <Card
                    isPressable
                    onPress={() => setActiveSkill(item)}
                    className="h-full w-full bg-default-100 p-2 shadow-sm transition-shadow duration-300 hover:shadow-md"
                  >
                    <div className="bg-content1 flex flex-1 flex-col rounded-xl">
                      <CardHeader className="items-center gap-3">
                        <div className="flex w-full min-w-0 flex-row items-center gap-3">
                          <div className="bg-default-100/10 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl">
                            <img
                              src={resolveSkillLogoSrc(item)}
                              alt={item.name}
                              className="h-12 w-12 rounded-lg object-contain"
                            />
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col gap-1 text-left">
                            <p
                              className="text-foreground text-[18px] leading-tight font-semibold"
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {item.name}
                            </p>
                            <p
                              className="text-default-400 text-xs font-medium leading-tight break-all"
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {item.id}
                            </p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardBody className="flex-1 pt-0">
                        <p
                          className="text-default-400 text-sm leading-relaxed"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {item.shortDescription}
                        </p>
                      </CardBody>
                    </div>
                    <CardFooter className="flex items-center justify-between pt-2 pb-0">
                      <div className="flex items-center gap-2">
                        <Avatar
                          src={resolveDeveloperAvatarSrc(item.developerLogo)}
                          name={resolveDeveloperAvatarName(item.developerLogo, item.developerName)}
                          size="sm"
                          className="bg-primary/20 text-primary !h-5 !w-5 !min-w-5 text-[9px]"
                        />
                        <span className="inline-flex items-center text-default-400 text-xs leading-none translate-y-[1px]">
                          {resolveDeveloperName(item.developerName)}
                        </span>
                      </div>
                      <span className="ml-auto inline-flex items-center gap-1 text-right text-default-400 text-xs leading-none translate-y-[1px]">
                        <Clock className="h-4 w-4 shrink-0" aria-hidden />
                        {formatSkillUpdatedAt(item.updatedAt)}
                      </span>
                    </CardFooter>
                  </Card>
                </m.div>
              ))}
            </div>

            {filteredSkills.length === 0 && (
              <Card className="border-default-200 border bg-content1/70">
                <CardBody className="py-10 text-center">
                  <p className="text-default-400 text-sm">没有找到匹配的技能，试试更短关键词或切换分类。</p>
                </CardBody>
              </Card>
            )}
          </m.section>
        </LazyMotion>
      </div>

      <Modal
        isOpen={Boolean(activeSkill)}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setActiveSkill(null);
          }
        }}
        size="lg"
        scrollBehavior="inside"
        backdrop="blur"
        placement="center"
        shouldBlockScroll
        classNames={{
          // 移动端保证四周 12px 间距，桌面端上下约 20px 间距
          wrapper: "items-center justify-center px-3 py-3 sm:px-4 sm:py-6 md:py-10",
          base:
            "bg-transparent shadow-none m-0 w-full max-w-[calc(100%-24px)] sm:max-w-[min(100%,520px)] min-h-0 max-h-[calc(100vh-40px)]",
          body: "p-0 flex flex-col min-h-0",
          closeButton: "top-3 right-3 z-10",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <div className="bg-white/95 text-foreground flex max-h-[calc(100vh-40px)] min-h-0 flex-col overflow-hidden rounded-[14px] border border-default-200 shadow-2xl">
              {/* 可滚动内容区域：高度自适应，超出时内部滚动 */}
              <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6 space-y-6">
                <div className="flex flex-col items-center gap-4">
                  {activeSkill && (
                    <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-[#f4f4f5]">
                      <img
                        src={resolveSkillLogoSrc(activeSkill)}
                        alt={activeSkill.name}
                        className="h-12 w-12 object-contain"
                      />
                    </div>
                  )}

                  <div className="flex flex-col items-center gap-2 text-center">
                    <p className="text-[18px] font-semibold leading-7 text-[#18181b]">
                      {activeSkill?.name ?? "小红书 - 超级前台"}
                    </p>
                    <p className="text-[14px] leading-5 text-[#a1a1aa]">
                      {activeSkill?.shortDescription ??
                        "评论区高情商秒回 + 后台私信自动跟进，让每一次咨询都更容易变成交"}
                    </p>
                  </div>
                </div>

                <div className="h-px w-full bg-[#e4e4e7]" />

                <div className="flex flex-col gap-3 text-[14px]">
                  <p className="text-[14px] font-semibold text-[#18181b]">适用场景</p>
                  <p className="text-[14px] leading-5 text-[#52525b]">
                    {activeSkill?.scenarioDescription ??
                      "适合通过评论区触达高意向用户、自动跟进私信和沉淀小红书公域线索到私域的场景。"}
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <p className="text-[14px] font-semibold text-[#18181b]">Prompt</p>
                  <div className="bg-default-100 flex max-h-56 flex-col gap-3 overflow-y-auto rounded-[14px] p-3 text-[14px] leading-5 text-[#52525b]">
                    <p>
                      {activeSkill?.prompt ??
                        "你现在要执行技能 xhs-competitor-outreach 的定时任务。\n任务名称：小红书竞品评论用户私信外联\n\n请直接使用 xhs-competitor-outreach 技能完成一次小红书竞品评论用户私信外联，不要输出方案说明，也不要只给脚本示例。"}
                    </p>
                  </div>
                </div>
              </div>

              {/* 底部按钮吸底：不随内容滚动 */}
              <ModalFooter className="shrink-0 justify-center border-t border-default-100/60 bg-content1/95 px-4 pb-3 pt-2 sm:px-6 sm:pb-4 sm:pt-3">
                <Button
                  color="primary"
                  radius="lg"
                  className="h-10 min-w-[96px] px-5 text-[14px] font-semibold"
                  onPress={() => {
                    onClose();
                  }}
                >
                  一键安装
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
